import React from 'react';
import { COLORS } from './config';

export const calculateOxidationRisk = (bottle, fillHistory = []) => {
  if (!bottle || bottle.status !== 'Open') return null;
  const fillLevel = bottle.fill_level || 100;
  const dateOpened = bottle.date_opened ? new Date(bottle.date_opened) : null;
  const today = new Date();
  const airExposure = ((100 - fillLevel) / 100) * 40;
  let daysOpenScore = 0;
  if (dateOpened) {
    const daysOpen = Math.floor((today - dateOpened) / (1000 * 60 * 60 * 24));
    daysOpenScore = Math.min((daysOpen / 365) * 30, 30);
  }
  let stagnationScore = 0;
  if (fillHistory.length >= 2) {
    const sorted = [...fillHistory].sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));
    const lastChange = new Date(sorted[0].recorded_at);
    const daysStagnant = Math.floor((today - lastChange) / (1000 * 60 * 60 * 24));
    stagnationScore = Math.min((daysStagnant / 180) * 30, 30);
  } else if (dateOpened) {
    const daysOpen = Math.floor((today - dateOpened) / (1000 * 60 * 60 * 24));
    stagnationScore = Math.min((daysOpen / 180) * 30, 30);
  }
  let baseScore = Math.min(Math.round(airExposure + daysOpenScore + stagnationScore), 100);
  const proof = bottle.proof || 80;
  let proofMultiplier = 1.0;
  if (proof >= 120) proofMultiplier = 0.65;
  else if (proof >= 110) proofMultiplier = 0.70;
  else if (proof >= 100) proofMultiplier = 0.80;
  else if (proof >= 90) proofMultiplier = 0.90;
  baseScore = Math.round(baseScore * proofMultiplier);
  const preservation = bottle.preservation_method || 'none';
  if (preservation === 'inert_gas') baseScore = Math.round(baseScore * 0.40);
  else if (preservation === 'decanted') baseScore = Math.round(baseScore * 0.50);
  else if (preservation === 'vacuum') baseScore = Math.round(baseScore * 0.70);
  return Math.min(baseScore, 100);
};

export const getRiskLevel = (score) => {
  if (score === null) return null;
  if (score <= 30) return { label: 'Low', color: COLORS.green };
  if (score <= 55) return { label: 'Moderate', color: COLORS.accent };
  if (score <= 75) return { label: 'High', color: '#E8772A' };
  return { label: 'Critical', color: COLORS.red };
};

export const getPredictedFinishDate = (bottle, fillHistory = []) => {
  if (!bottle || bottle.status !== 'Open' || fillHistory.length < 2) return null;
  const sorted = [...fillHistory].sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const daysDiff = Math.floor((new Date(last.recorded_at) - new Date(first.recorded_at)) / (1000 * 60 * 60 * 24));
  if (daysDiff === 0) return null;
  const fillDiff = first.fill_level - last.fill_level;
  if (fillDiff <= 0) return null;
  const ratePerDay = fillDiff / daysDiff;
  const daysRemaining = Math.floor(bottle.fill_level / ratePerDay);
  const finishDate = new Date();
  finishDate.setDate(finishDate.getDate() + daysRemaining);
  return finishDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const getPourCount = (fillLevel, volumeMl = 750) => Math.round((fillLevel / 100) * (volumeMl / 44.4));

export const getPreservationLabel = (method) => {
  const labels = {
    none: 'None',
    vacuum: 'Vacuum Stopper',
    inert_gas: 'Inert Gas (Private Preserve)',
    decanted: 'Decanted to Smaller Bottle',
  };
  return labels[method] || 'None';
};

export const getProofAdjustmentLabel = (proof) => {
  if (!proof) return null;
  if (proof >= 120) return 'High proof — 35% oxidation reduction applied';
  if (proof >= 110) return 'High proof — 30% oxidation reduction applied';
  if (proof >= 100) return 'High proof — 20% oxidation reduction applied';
  if (proof >= 90) return 'High proof — 10% oxidation reduction applied';
  return null;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const getScoreColor = (score) => {
  if (score >= 9) return COLORS.green;
  if (score >= 7) return COLORS.accent;
  return COLORS.textMuted;
};

export const getFillColor = (fill) => {
  if (fill > 60) return COLORS.green;
  if (fill > 30) return COLORS.accent;
  return COLORS.red;
};

export const buildScoreMap = (tastingNotes) => {
  const map = {};
  tastingNotes.forEach(note => {
    if (note.bottle_id && note.overall_score) {
      if (!map[note.bottle_id]) map[note.bottle_id] = { total: 0, count: 0 };
      map[note.bottle_id].total += note.overall_score;
      map[note.bottle_id].count += 1;
    }
  });
  return map;
};

export const getAvgScore = (scoreMap, bottleId) => {
  const entry = scoreMap[bottleId] || scoreMap[String(bottleId)];
  return entry ? Math.round(entry.total / entry.count) : null;
};

export const getStatusStyle = (status) => {
  switch (status) {
    case 'Sealed':  return { bg: COLORS.statusSealed,    text: '#7DBF8A' };
    case 'Open':    return { bg: COLORS.statusOpen,      text: '#C6922B' };
    case 'Emptied': return { bg: COLORS.statusEmpty,     text: '#8A7A5C' };
    case 'Gifted':  return { bg: COLORS.statusEmpty,     text: '#8A7A5C' };
    default:        return { bg: COLORS.statusEmpty,     text: '#8A7A5C' };
  }
};

export const s = {
  app: {
    backgroundColor: COLORS.bg,
    minHeight: '100vh',
    maxWidth: 430,
    margin: '0 auto',
    fontFamily: 'Georgia, serif',
    color: COLORS.text,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },

  header: {
    padding: '20px 20px 12px',
    borderBottom: `1px solid ${COLORS.border}`,
    background: `linear-gradient(180deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 40%, ${COLORS.bg} 100%)`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  logo: { fontSize: 22, fontWeight: 'bold', color: COLORS.accent, letterSpacing: 2, margin: 0 },
  logoSub: { fontSize: 10, color: COLORS.textMuted, letterSpacing: 4, marginTop: 2 },
  screen: { flex: 1, padding: 16, overflowY: 'auto', paddingBottom: 80 },

  nav: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '10px 0 20px',
    borderTop: `1px solid ${COLORS.border}`,
    background: COLORS.bg,
    position: 'fixed',
    bottom: 0,
    width: '100%',
    maxWidth: 430,
  },

  navBtn: (active) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: active ? COLORS.accent : COLORS.textMuted,
    fontSize: 10,
    letterSpacing: 1,
    padding: '4px 12px',
  }),

  navIcon: { fontSize: 18 },

  card: {
    background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 18,
    marginBottom: 12,
    boxShadow: `0 4px 16px rgba(0,0,0,0.6)`,
  },

  cardTitle: {
    fontSize: 10,
    color: COLORS.accent,
    letterSpacing: 3,
    marginBottom: 10,
    margin: '0 0 10px 0',
    textTransform: 'uppercase',
  },

  statRow: { display: 'flex', gap: 8, marginBottom: 14 },

  statCard: {
    flex: 1,
    background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 12,
    textAlign: 'center',
    cursor: 'pointer',
    boxShadow: `0 4px 12px rgba(0,0,0,0.5)`,
  },

  statNum: { fontSize: 24, fontWeight: 'bold', color: COLORS.accent, margin: 0 },
  statLabel: { fontSize: 9, color: COLORS.textMuted, letterSpacing: 2, textTransform: 'uppercase', marginTop: 3, display: 'block' },

  bottleCard: {
    background: `linear-gradient(135deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    boxShadow: `0 4px 14px rgba(0,0,0,0.55)`,
  },

  bottleName: { fontSize: 18, fontWeight: 'bold', color: COLORS.text, margin: '0 0 4px 0' },
  bottleMeta: { fontSize: 12, color: COLORS.textMuted, margin: '0 0 6px 0' },

  tag: (color = COLORS.accent) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 10,
    backgroundColor: color + '18',
    color: color,
    border: `1px solid ${color}30`,
    letterSpacing: 0.5,
  }),

  fillBarBg: {
    height: 5,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 3,
    marginTop: 10,
    overflow: 'hidden',
  },

  sectionTitle: {
    fontSize: 10,
    color: COLORS.textMuted,
    letterSpacing: 3,
    textTransform: 'uppercase',
    marginBottom: 12,
  },

  alertBanner: {
    background: `linear-gradient(135deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
    border: `1px solid ${COLORS.accent}30`,
    borderRadius: 12,
    padding: '10px 14px',
    marginBottom: 12,
  },

  warningBanner: {
    backgroundColor: COLORS.red + '12',
    border: `1px solid ${COLORS.red}28`,
    borderRadius: 12,
    padding: '10px 14px',
    marginBottom: 12,
    fontSize: 12,
    color: COLORS.red,
  },

  input: {
    backgroundColor: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: '10px 14px',
    color: COLORS.text,
    fontFamily: 'Georgia, serif',
    fontSize: 13,
    width: '100%',
    marginBottom: 12,
    boxSizing: 'border-box',
    outline: 'none',
  },

  select: {
    backgroundColor: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: '10px 14px',
    color: COLORS.text,
    fontFamily: 'Georgia, serif',
    fontSize: 13,
    width: '100%',
    marginBottom: 12,
    boxSizing: 'border-box',
    outline: 'none',
    cursor: 'pointer',
  },

  label: {
    fontSize: 10,
    color: COLORS.textMuted,
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 6,
    display: 'block',
  },

  filterRow: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },

  filterChip: (active) => ({
    padding: '7px 16px',
    borderRadius: 20,
    fontSize: 11,
    border: `1px solid ${active ? COLORS.accentDark : 'rgba(144, 115, 85, 0.35)'}`,
    color: active ? COLORS.bg : COLORS.textMuted,
    background: active
      ? `linear-gradient(180deg, ${COLORS.addBtnTop} 0%, ${COLORS.addBtnBottom} 100%)`
      : 'rgba(255,255,255,0.03)',
    cursor: 'pointer',
    letterSpacing: 0.5,
    fontFamily: 'Georgia, serif',
    fontWeight: active ? 'bold' : 'normal',
    boxShadow: active ? '0 2px 6px rgba(0,0,0,0.35)' : 'none',
  }),

  primaryBtn: {
    background: `linear-gradient(135deg, ${COLORS.accentLight} 0%, ${COLORS.accent} 60%, ${COLORS.accentDark} 100%)`,
    color: COLORS.bg,
    border: 'none',
    borderRadius: 8,
    padding: '14px 20px',
    fontFamily: 'Georgia, serif',
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: 1,
    cursor: 'pointer',
    width: '100%',
    marginBottom: 10,
    boxShadow: `0 2px 10px rgba(198,146,43,0.35)`,
  },

  addBtn: {
    background: `linear-gradient(180deg, ${COLORS.addBtnTop} 0%, ${COLORS.addBtnBottom} 100%)`,
    color: COLORS.bg,
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    fontFamily: 'Georgia, serif',
    fontSize: 13,
    fontWeight: 'bold',
    letterSpacing: 1,
    cursor: 'pointer',
    marginBottom: 0,
    boxShadow: `0 2px 8px rgba(0,0,0,0.4)`,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  secondaryBtn: {
    backgroundColor: 'transparent',
    color: COLORS.accent,
    border: `1px solid ${COLORS.accent}`,
    borderRadius: 8,
    padding: '12px 20px',
    fontFamily: 'Georgia, serif',
    fontSize: 13,
    cursor: 'pointer',
    width: '100%',
    marginBottom: 10,
  },

  dangerBtn: {
    backgroundColor: 'transparent',
    color: COLORS.red,
    border: `1px solid ${COLORS.red}`,
    borderRadius: 8,
    padding: '12px 20px',
    fontFamily: 'Georgia, serif',
    fontSize: 13,
    cursor: 'pointer',
    width: '100%',
    marginBottom: 10,
  },

  backBtn: {
    background: 'none',
    border: 'none',
    color: COLORS.accent,
    fontSize: 14,
    cursor: 'pointer',
    letterSpacing: 1,
    padding: '0 0 16px 0',
    display: 'block',
    fontFamily: 'Georgia, serif',
  },

  detailRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 },
  divider: { height: 1, backgroundColor: COLORS.border, margin: '12px 0' },
  scoreRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },

  textarea: {
    backgroundColor: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: 10,
    color: COLORS.text,
    fontFamily: 'Georgia, serif',
    fontSize: 13,
    width: '100%',
    marginBottom: 10,
    minHeight: 70,
    boxSizing: 'border-box',
    resize: 'none',
    outline: 'none',
  },

  flavorGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },

  wishCard: {
    background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
    boxShadow: `0 4px 12px rgba(0,0,0,0.5)`,
  },

  successBanner: {
    backgroundColor: COLORS.green + '20',
    border: `1px solid ${COLORS.green}40`,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 12,
    color: COLORS.green,
    fontSize: 13,
    textAlign: 'center',
  },

  errorBanner: {
    backgroundColor: COLORS.red + '20',
    border: `1px solid ${COLORS.red}40`,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 12,
    color: COLORS.red,
    fontSize: 13,
    textAlign: 'center',
  },

  tastingNoteCard: {
    background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
    boxShadow: `0 4px 12px rgba(0,0,0,0.5)`,
  },

  tastingNoteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
    cursor: 'pointer',
  },

  scoreCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },

  riskCard: (color) => ({
    backgroundColor: color + '12',
    border: `1px solid ${color}30`,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),

  deleteModal: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.88)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },

  deleteModalCard: {
    background: `linear-gradient(160deg, ${COLORS.bgCardElevated} 0%, ${COLORS.bgCard} 100%)`,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 380,
    boxShadow: `0 8px 32px rgba(0,0,0,0.7)`,
  },

  lookupOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.93)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    flexDirection: 'column',
    gap: 20,
    padding: 30,
  },

  stepIndicator: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 20,
  },

  sliderRow: { marginBottom: 16 },

  sliderLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
  },

  infoBox: {
    backgroundColor: COLORS.accent + '0E',
    border: `1px solid ${COLORS.accent}28`,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 12,
    fontSize: 12,
    color: COLORS.textMuted,
    lineHeight: 1.6,
  },
};

export const FlavorSection = ({ title, tags, selected, onToggle }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 8 }}>{title}</div>
    <div style={s.flavorGrid}>
      {tags.map(f => (
        <button key={f} onClick={() => onToggle(f)} style={{
          padding: '6px 12px',
          borderRadius: 30,
          border: `1px solid ${selected.includes(f) ? COLORS.accent : COLORS.border}`,
          backgroundColor: selected.includes(f) ? COLORS.accent + '20' : 'transparent',
          color: selected.includes(f) ? COLORS.accent : COLORS.textMuted,
          fontSize: 11,
          cursor: 'pointer',
          fontFamily: 'Georgia, serif',
        }}>{f}</button>
      ))}
    </div>
  </div>
);

export const TastingSlider = ({ label, leftLabel, rightLabel, value, onChange }) => (
  <div style={s.sliderRow}>
    <div style={{ fontSize: 11, color: COLORS.text, marginBottom: 6, letterSpacing: 1 }}>{label}</div>
    <input
      type="range" min={1} max={5} value={value}
      onChange={e => onChange(parseInt(e.target.value))}
      style={{ width: '100%', accentColor: COLORS.accent }}
    />
    <div style={s.sliderLabels}>
      <span style={{ fontSize: 10, color: COLORS.textMuted }}>{leftLabel}</span>
      <span style={{ fontSize: 10, color: COLORS.accent, fontWeight: 'bold' }}>{value}/5</span>
      <span style={{ fontSize: 10, color: COLORS.textMuted }}>{rightLabel}</span>
    </div>
  </div>
);

export const StepIndicator = ({ current, total }) => (
  <div style={s.stepIndicator}>
    {Array.from({ length: total }, (_, i) => (
      <div key={i} style={{
        width: i + 1 === current ? 24 : 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: i + 1 <= current ? COLORS.accent : COLORS.border,
        transition: 'all 0.2s ease',
      }} />
    ))}
  </div>
);

export const BottleCard = ({ bottle, avgScore, onClick }) => {
  const statusStyle = getStatusStyle(bottle.status);
  return (
    <div style={s.bottleCard} onClick={onClick}>
      <div style={{ flex: 1 }}>
        <p style={s.bottleName}>{bottle.expression_name}</p>
        <div style={{
          paddingLeft: 10,
          borderLeft: `2px solid ${COLORS.accent}20`,
          marginTop: 4,
        }}>
          <p style={s.bottleMeta}>{bottle.distillery} • {bottle.proof} proof</p>
          {bottle.nickname && (
            <p style={{ fontSize: 11, color: COLORS.accent, fontStyle: 'italic', margin: '0 0 6px 0' }}>
              "{bottle.nickname}"
            </p>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            <span style={{
              display: 'inline-block',
              padding: '3px 10px',
              borderRadius: 20,
              fontSize: 10,
              backgroundColor: statusStyle.bg,
              color: statusStyle.text,
              border: `1px solid ${statusStyle.text}28`,
              letterSpacing: 0.5,
            }}>{bottle.status}</span>
            {bottle.personal_tier && (
              <span style={s.tag(
                bottle.personal_tier === 'Legendary' ? COLORS.accentLight
                : bottle.personal_tier === 'Premium' ? COLORS.accent
                : COLORS.textMuted
              )}>{bottle.personal_tier}</span>
            )}
            {bottle.limited_allocated && (
              <span style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 20,
                fontSize: 10,
                backgroundColor: COLORS.statusAllocated,
                color: '#C07060',
                border: '1px solid rgba(192,112,96,0.25)',
                letterSpacing: 0.5,
              }}>Allocated</span>
            )}
            {bottle.is_finished && <span style={s.tag('#7B52A0')}>Finished</span>}
            {avgScore && <span style={s.tag(getScoreColor(avgScore))}>⭐ {avgScore}</span>}
          </div>
          {bottle.status === 'Open' && bottle.fill_level && (
            <div style={s.fillBarBg}>
              <div style={{
                height: '100%',
                width: `${bottle.fill_level}%`,
                background: `linear-gradient(90deg, ${COLORS.fillBarStart}, ${COLORS.fillBarMid}, ${COLORS.fillBarEnd})`,
                borderRadius: 3,
              }} />
            </div>
          )}
        </div>
      </div>
      <span style={{ fontSize: 18, color: COLORS.textDim }}>›</span>
    </div>
  );
};

export const DeleteBottleModal = ({ bottle, tastingNoteCount, onConfirm, onCancel }) => {
  const [deleteChoice, setDeleteChoice] = React.useState('keep');
  return (
    <div style={s.deleteModal}>
      <div style={s.deleteModalCard}>
        <h3 style={{ color: COLORS.red, margin: '0 0 8px 0', fontSize: 16 }}>Delete {bottle.expression_name}?</h3>
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 16px 0' }}>This cannot be undone.</p>
        {tastingNoteCount > 0 && (
          <>
            <div style={{ ...s.warningBanner, marginBottom: 16 }}>
              This bottle has {tastingNoteCount} tasting note{tastingNoteCount > 1 ? 's' : ''}.
            </div>
            <p style={{ color: COLORS.text, fontSize: 13, marginBottom: 12, fontWeight: 'bold' }}>
              What should happen to the tasting notes?
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                <input type="radio" value="keep" checked={deleteChoice === 'keep'}
                  onChange={() => setDeleteChoice('keep')}
                  style={{ accentColor: COLORS.accent, marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 'bold' }}>Keep as experience notes</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>Notes stay in your flavor profile.</div>
                </div>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="radio" value="delete" checked={deleteChoice === 'delete'}
                  onChange={() => setDeleteChoice('delete')}
                  style={{ accentColor: COLORS.red, marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 13, color: COLORS.red, fontWeight: 'bold' }}>Delete everything</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>Remove bottle and all tasting notes.</div>
                </div>
              </label>
            </div>
          </>
        )}
        <button style={{ ...s.dangerBtn, marginBottom: 8 }} onClick={() => onConfirm(deleteChoice)}>
          {tastingNoteCount > 0 && deleteChoice === 'keep' ? 'Delete Bottle, Keep Notes' : 'Delete Everything'}
        </button>
        <button style={s.secondaryBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

export const DeleteNoteModal = ({ onConfirm, onCancel }) => (
  <div style={s.deleteModal}>
    <div style={s.deleteModalCard}>
      <h3 style={{ color: COLORS.red, margin: '0 0 8px 0', fontSize: 16 }}>Delete Tasting Note?</h3>
      <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 16px 0' }}>
        This tasting session will be permanently removed from your history.
      </p>
      <button style={{ ...s.dangerBtn, marginBottom: 8 }} onClick={onConfirm}>Delete Note</button>
      <button style={s.secondaryBtn} onClick={onCancel}>Cancel</button>
    </div>
  </div>
);

// ── TwoTierChips ─────────────────────────────────────────────────────────────
// taxonomy: { 'Sweet': ['Vanilla', 'Honey', ...], ... }
// broadSelected: string[] — active broad chips
// specificSelected: string[] — active tier 2 chips
// onBroadToggle(cat) — tap broad chip
// onSpecificToggle(tag) — tap tier 2 chip
export const TwoTierChips = ({ taxonomy, broadSelected, specificSelected, onBroadToggle, onSpecificToggle }) => (
  <div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      {Object.keys(taxonomy).map(cat => {
        const active = broadSelected.includes(cat);
        return (
          <button key={cat} onClick={() => onBroadToggle(cat)} style={{
            padding: '8px 14px', borderRadius: 20,
            border: `1px solid ${active ? COLORS.accent : 'rgba(144,115,85,0.35)'}`,
            background: active
              ? `linear-gradient(135deg, ${COLORS.accentLight}22, ${COLORS.accent}33)`
              : 'transparent',
            color: active ? COLORS.accentLight : COLORS.textMuted,
            fontSize: 13, fontFamily: 'Georgia, serif', cursor: 'pointer',
            fontWeight: active ? 'bold' : 'normal', transition: 'all 0.15s',
          }}>{cat}</button>
        );
      })}
    </div>
    {Object.keys(taxonomy).map(cat => {
      if (!broadSelected.includes(cat)) return null;
      return (
        <div key={cat} style={{
          marginBottom: 10,
          paddingLeft: 10,
          borderLeft: `2px solid ${COLORS.accent}40`,
        }}>
          <div style={{ fontSize: 9, color: COLORS.accent, letterSpacing: 2, marginBottom: 6, textTransform: 'uppercase' }}>
            {cat}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {taxonomy[cat].map(tag => {
              const active = specificSelected.includes(tag);
              return (
                <button key={tag} onClick={() => onSpecificToggle(tag)} style={{
                  padding: '6px 12px', borderRadius: 20,
                  border: `1px solid ${active ? COLORS.accentLight : COLORS.border}`,
                  background: active ? COLORS.accentLight + '22' : 'transparent',
                  color: active ? COLORS.accentLight : COLORS.textDim,
                  fontSize: 11, fontFamily: 'Georgia, serif', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}>{tag}</button>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

// ── ExpansionCard — collapsible per-phase detail section ──────────────────────
export const ExpansionCard = ({ label, children, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ marginTop: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6, padding: 0,
        color: COLORS.textMuted, fontFamily: 'Georgia, serif', fontSize: 11,
        letterSpacing: 1, textTransform: 'uppercase',
      }}>
        <span style={{
          fontSize: 9, color: COLORS.accent,
          transition: 'transform 0.2s',
          display: 'inline-block',
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
        }}>▶</span>
        {label}
      </button>
      {open && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.border}` }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── ColorSlider — 6-stop bourbon color gradient selector ──────────────────────
export const ColorSlider = ({ value, onChange, stops }) => {
  const idx = stops.indexOf(value);
  const current = idx >= 0 ? idx : 0;
  const gradient = 'linear-gradient(90deg, #F5E8C0, #C8A84B, #A0722A, #7A4E1E, #6B3A1F, #3D1C0E)';
  return (
    <div>
      <div style={{
        height: 12, borderRadius: 6,
        background: gradient,
        marginBottom: 10,
      }} />
      <input
        type="range" min={0} max={stops.length - 1}
        value={current}
        onChange={e => onChange(stops[parseInt(e.target.value)])}
        style={{ width: '100%', accentColor: COLORS.accent, marginBottom: 6 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {stops.map((stop, i) => (
          <span key={stop} style={{
            fontSize: 8, color: i === current ? COLORS.accentLight : COLORS.textDim,
            fontFamily: 'Georgia, serif', letterSpacing: 0.3,
            fontWeight: i === current ? 'bold' : 'normal',
            textAlign: 'center', flex: 1,
          }}>{stop.replace(' ', '\n')}</span>
        ))}
      </div>
    </div>
  );
};

// ── MultiChips — single-select or multi-select chip row ───────────────────────
// Pass selected as string (single-select) or string[] (multi-select)
// onToggle receives the option value
export const MultiChips = ({ options, selected, onToggle }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
    {options.map(opt => {
      const active = Array.isArray(selected) ? selected.includes(opt) : selected === opt;
      return (
        <button key={opt} onClick={() => onToggle(opt)} style={{
          padding: '7px 13px', borderRadius: 20,
          border: `1px solid ${active ? COLORS.accent : 'rgba(144,115,85,0.35)'}`,
          background: active
            ? `linear-gradient(135deg, ${COLORS.accentLight}22, ${COLORS.accent}33)`
            : 'transparent',
          color: active ? COLORS.accentLight : COLORS.textMuted,
          fontSize: 12, fontFamily: 'Georgia, serif', cursor: 'pointer',
          fontWeight: active ? 'bold' : 'normal', transition: 'all 0.15s',
        }}>{opt}</button>
      );
    })}
  </div>
);
